import React, { useState } from 'react';

import { Box, Button, Grid, Typography } from '@mui/material';

import AttackChartMUI from './AttackChartMUI';
import KingdomProvAttacks from './KingdomProvAttacks';
import KingdomUniques from './KingdomUniques';
import Stat from './Stat';
import {
  KingdomAttacksByProvince,
  KingdomNews,
} from '../../@types/KingdomNews';
import { useIntelState } from '../../context/IntelState';

const buttonStyles = { height: '30px' };

const KdNewsReport: React.FC = () => {
  const { intelState } = useIntelState();
  const [showGraph, setShowGraph] = useState<boolean>(false);
  const kdNews: KingdomNews | undefined = intelState.kingdomNews;
  const getEnemyUniques = () => {
    let total = 0;
    kdNews &&
      kdNews.enemyKdsAndProvinceList.forEach((kd) => {
        total += kd.attacksByProvinces.reduce((p, v) => v.uniques + p, 0);
      });
    return total;
  };
  if (!kdNews) return null;
  return (
    <>
      <Typography variant='h3' gutterBottom component='div'>
        Kingdom News Report
      </Typography>
      <Typography variant='h5' gutterBottom component='div'>
        {kdNews.startDate.fullDateString} - {kdNews.endDate.fullDateString} (
        {kdNews.numberOfDays} days)
      </Typography>
      <Button
        variant='outlined'
        onClick={() => setShowGraph(!showGraph)}
        sx={buttonStyles}
        size='small'
      >
        {showGraph ? 'Hide' : 'Show'} Graph
      </Button>
      {showGraph && (
        <>
          <Box sx={{ width: '90vw', padding: 1, height: '400px' }}>
            <AttackChartMUI chartData={kdNews.chartData} />
          </Box>
        </>
      )}
      <Grid
        container
        justifyContent='flex-start'
        direction='row'
        alignItems='flex-start'
        spacing={2}
        mt={1}
      >
        <Grid
          style={{ display: 'flex', flexDirection: 'column' }}
          size={{
            lg: 3,
            md: 4,
            sm: 6,
            xs: 12
          }}>
          <Stat
            variant='body1'
            label='Total Attacks Made'
            total={kdNews.attacksMade}
            gains={kdNews.acresGain}
          />
          <Stat
            label='Uniques'
            total={kdNews.kingdomAttacksByProvince.attacksByProvinces.reduce(
              (p, v) => v.uniques + p,
              0
            )}
          />
          <Stat
            label='Trad March'
            total={kdNews.tradMarches?.attacksMade}
            gains={kdNews.tradMarches?.gains}
          />
          <Stat
            label='Ambush'
            total={kdNews.ambushes?.attacksMade}
            gains={kdNews.ambushes?.gains}
          />
          <Stat
            label='Conquest'
            total={kdNews.conquests?.attacksMade}
            gains={kdNews.conquests?.gains}
          />
          <Stat
            label='Raze'
            total={kdNews.razes?.attacksMade}
            gains={kdNews.razes?.gains}
          />
          <Stat
            label='Learn'
            total={kdNews.learns?.attacksMade}
            gains={kdNews.learns?.gains}
            gainLabel='books'
          />
          <Stat
            label='Massacre'
            total={kdNews.massacres?.attacksMade}
            gains={kdNews.massacres?.gains}
            gainLabel='people'
          />
          <Stat label='Plunder' total={kdNews.plunders.attacksMade} />
          <Stat label='Bounces' total={kdNews.failedAttacks.attacksMade} />
          <Stat label='DragonsStarted' total={kdNews.dragonsStarted} />
          <Stat label='DragonsCompleted' total={kdNews.dragonsCompleted} />
          <Stat label='Enemy Dragons Killed' total={kdNews.dragonsKilled} />
          <Stat label='Rituals Started' total={kdNews.ritualsStarted} />
          <Stat label='Rituals Completed' total={kdNews.ritualsCompleted} />
        </Grid>
        <Grid
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
          size={{
            lg: 3,
            md: 4,
            sm: 6,
            xs: 12
          }}>
          <Stat
            variant='body1'
            label='Total Attacks Suffered'
            total={kdNews.attacksSuffered}
            gains={kdNews.acresLoss}
          />
          <Stat label='Uniques' total={getEnemyUniques()} />
          <Stat
            label='Trad March'
            total={kdNews.tradMarches?.attacksSuffered}
            gains={kdNews.tradMarches?.gainsSuffered}
          />
          <Stat
            label='Ambush'
            total={kdNews.ambushes?.attacksSuffered}
            gains={kdNews.ambushes?.gainsSuffered}
          />
          <Stat
            label='Conquest'
            total={kdNews.conquests?.attacksSuffered}
            gains={kdNews.conquests?.gainsSuffered}
          />
          <Stat
            label='Raze'
            total={kdNews.razes?.attacksSuffered}
            gains={kdNews.razes?.gainsSuffered}
          />
          <Stat
            label='Learn'
            total={kdNews.learns?.attacksSuffered}
            gains={kdNews.learns?.gainsSuffered}
            gainLabel='books'
          />
          <Stat
            label='Massacre'
            total={kdNews.massacres?.attacksSuffered}
            gains={kdNews.massacres.gainsSuffered}
            gainLabel='people'
          />
          <Stat label='Plunder' total={kdNews.plunders?.attacksSuffered} />
          <Stat label='Bounces' total={kdNews.failedAttacks?.attacksSuffered} />
          <Stat label='Dragons Started' total={kdNews.enemyDragonsStarted} />
          <Stat label='Dragons Completed' total={kdNews.dragonsReceived} />
        </Grid>
      </Grid>
      <Grid
        container
        justifyContent='flex-start'
        direction='row'
        alignItems='flex-start'
        spacing={2}
        mt={1}
      >
        <KingdomProvAttacks
          label='Own Kingdom'
          key={kdNews.kingdomAttacksByProvince.kingdomReference}
          kingdom={kdNews.kingdomAttacksByProvince}
          ownKd
        />
        {kdNews.enemyKdsAndProvinceList.map((k: KingdomAttacksByProvince) => (
          <KingdomProvAttacks
            label='The Kingdom of'
            key={k.kingdomReference}
            kingdom={k}
          />
        ))}
      </Grid>
      <Grid
        container
        justifyContent='flex-start'
        direction='row'
        alignItems='flex-start'
        spacing={2}
        mt={1}
      >
        <KingdomUniques
          key={kdNews.kingdomAttacksByProvince.kingdomReference}
          kingdom={kdNews.kingdomAttacksByProvince}
        />
        {kdNews.enemyKdsAndProvinceList.map((k: KingdomAttacksByProvince) => (
          <KingdomUniques key={k.kingdomReference} kingdom={k} />
        ))}
      </Grid>
      <Grid
        container
        justifyContent='flex-start'
        direction='row'
        alignItems='flex-start'
        spacing={2}
        mt={1}
        mb={3}
      >
        <Grid style={{ display: 'flex', flexDirection: 'column' }} size={12}>
          <Typography
            variant='body1'
            gutterBottom
            component='div'
            sx={{ fontWeight: 'bold' }}
          >
            Highlights
          </Typography>
          <Typography variant={'body2'} component='div'>
            Most land gained in a single trad march -{' '}
            {kdNews.biggestLandGain?.name}: {kdNews.biggestLandGain?.gains}
          </Typography>
          <Typography variant={'body2'} component='div'>
            Most land lost in a single trad march -{' '}
            {kdNews.biggestLandLoss?.name}: {kdNews.biggestLandLoss?.gains}
          </Typography>
          <Typography variant={'body2'} component='div'>
            Most land gained in a single ambush -{' '}
            {kdNews.biggestAmbushGain?.name}: {kdNews.biggestAmbushGain?.gains}
          </Typography>
          <Typography variant={'body2'} component='div'>
            Most land lost in a single ambush - {kdNews.biggestAmbushLoss?.name}
            : {kdNews.biggestAmbushLoss?.gains}
          </Typography>
          <Typography variant={'body2'} component='div'>
            Most bounces made - {kdNews.mostBouncesMade?.name}:{' '}
            {kdNews.mostBouncesMade?.gains}
          </Typography>
          <Typography variant={'body2'} component='div'>
            Most bounces received - {kdNews.mostBouncesReceived?.name}:{' '}
            {kdNews.mostBouncesReceived?.gains}
          </Typography>
        </Grid>
      </Grid>
    </>
  );
};

export default KdNewsReport;
